import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useRouteMatch } from '@react-router';

import { useRoutePaths } from '@apps/registry/admin/Registry.routes';
import { SwatchColorOption } from '@apps/registry/common/components/SwatchColorGrid/types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCurrencyFormatter } from '@shared/utils/currency';

import { getProductTileDisplayFields } from '../../Catalog.helpers';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { PagesForTelemetry, ShopItemClickedFromComponent } from '../../Catalog.telemetry.types';

import { findMatchingProductByDimension } from '../../utils/colorSwatchFeature';
import { isCashOrCharityCatalogItem } from '../../utils/interpretItemType';
import { useCatalogContext } from '../../state/Catalog';

import { useCatalogCategorySectionContext } from '../CatalogCategorySectionProvider';

import { useMobileProductQuickAddDrawerProvider } from './components/MobileProductQuickAddDrawer';
import { ProductTileProps } from './ProductTile.types';
import { MonetaryValue } from './components/BaseProductDetails/BaseProductDetails.types';

export const useProductTile = (props: ProductTileProps) => {
  const { deriveTelemetryPageForProduct, product, shouldOpenPdp, componentSource, categoryType, addItemLoading, index, handleClickForCashDialog } = props;
  const [clicked, setClicked] = useState(false);
  const [selectedProduct, setProductDimension] = useState(product);

  const routes = useRoutePaths();
  const location = useLocation();
  const isOverviewRouteMatch = useRouteMatch({ path: routes.overview.path, exact: true });

  const catalogCategorySectionContext = useCatalogCategorySectionContext(componentSource === 'categoryRec');
  const { trackProductTileClickAction, trackItemVariantSelectAction } = useCatalogLoggingContext();
  const { selectedCategory, goToCatalogItem, isProductInRegistry, onOpenEditGiftDialog } = useCatalogContext();
  const { formatCurrency } = useCurrencyFormatter();
  const { handleChangeCurrentProductIndex, handleChangeCurrentProductId, openDrawer, closeDrawer } = useMobileProductQuickAddDrawerProvider();

  const isTypeCashOrCharity = isCashOrCharityCatalogItem(product);

  const formatPrice = (price: MonetaryValue) => {
    return formatCurrency({
      priceFloatingPointDecimalString: price?.floatingPointDecimalString || '',
      priceCurrencyCode: price?.currency?.code,
      formatForm: 'short',
    });
  };

  const telemetryPageValue: PagesForTelemetry = deriveTelemetryPageForProduct({
    defaultValue: categoryType === 'curation' ? 'curationDetail' : 'root',
    product: selectedProduct,
    view: 'productGrid',
    selectedCategory,
  });

  const productDisplayFields = useMemo(() => {
    return getProductTileDisplayFields(selectedProduct);
  }, [selectedProduct]);

  const swatchColorOptions: SwatchColorOption[] = useMemo(() => {
    const productionDimensionOptions = product.group?.tileViewDimension?.options || [];
    return productionDimensionOptions.map(pd => {
      return {
        color: pd.swatchColor?.hex,
        id: pd.id,
        imageUrl: null,
        label: pd.displayString,
      };
    });
  }, [product]);

  // this
  const changeActiveDimensionOption = useCallback(
    (color: SwatchColorOption) => {
      const selectedOptionDimension = findMatchingProductByDimension({ parentProduct: selectedProduct, dimensionOptionId: color.id });
      if (selectedOptionDimension && selectedOptionDimension?.photos) {
        setProductDimension({
          ...product,
          id: selectedOptionDimension.id,
          price: selectedOptionDimension?.price,
          photos: selectedOptionDimension?.photos,
        });

        trackItemVariantSelectAction({
          product,
          groupDimensionOption: color.label,
          groupDimensionTitle: 'Color',
          dimensionStringValue: color.color,
          dimensionType: 'color',
          method: 'productTileSwatch',
          page: telemetryPageValue,
        });
      }
    },
    [product, selectedProduct, trackItemVariantSelectAction, telemetryPageValue],
  );

  const handleMobileQuickAddClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, selectedOptionId?: string) => {
      e.stopPropagation();
      const selectedOptionDimension = selectedOptionId ? findMatchingProductByDimension({ parentProduct: selectedProduct, dimensionOptionId: selectedOptionId }) : null;
      const selectedProductId = selectedOptionDimension ? selectedOptionDimension.id : selectedProduct.id;

      handleChangeCurrentProductIndex(index);
      handleChangeCurrentProductId(selectedProductId);
      openDrawer();
    },
    [handleChangeCurrentProductId, handleChangeCurrentProductIndex, index, openDrawer, selectedProduct],
  );

  const handleClick = useEventCallback((e: React.MouseEvent<HTMLButtonElement | HTMLLinkElement, MouseEvent>) => {
    e.stopPropagation();
    setClicked(true);
    if (isTypeCashOrCharity) {
      const isQuickAdd = !!e.currentTarget.dataset.quickadd;
      handleClickForCashDialog?.({
        clickedProduct: selectedProduct,
        isQuickAdd,
        itemPosition: index,
      });
    }
  });

  const handleOnViewMoreDimensionClick = useEventCallback<React.MouseEventHandler>(e => {
    e.stopPropagation();
    showProductDetailPage(
      componentSource === 'categoryRec'
        ? {
            component: 'categoryRec',
            method: 'carouselProductTileSwatch',
            sectionTitle: catalogCategorySectionContext?.title,
            sectionCategoryId: catalogCategorySectionContext?.id,
          }
        : { method: 'productTileSwatch', component: componentSource },
    );
  });

  const handleDetailKeyPress = useEventCallback<React.KeyboardEventHandler>(e => {
    e.stopPropagation();
    showProductDetailPage(
      componentSource === 'categoryRec'
        ? {
            component: 'categoryRec',
            method: 'carouselProductTile',
            sectionTitle: catalogCategorySectionContext?.title,
            sectionCategoryId: catalogCategorySectionContext?.id,
          }
        : { method: 'productTile', component: componentSource },
    );
  });

  const handleDetailClick = useEventCallback((e: React.MouseEvent<Element, MouseEvent>) => {
    const isDirectClick = !e.ctrlKey && !e.metaKey;
    if (isDirectClick) {
      e.preventDefault();
    }
    closeDrawer();
    e.stopPropagation();
    showProductDetailPage(
      componentSource === 'categoryRec'
        ? {
            component: 'categoryRec',
            method: 'carouselProductTile',
            sectionTitle: catalogCategorySectionContext?.title,
            sectionCategoryId: catalogCategorySectionContext?.id,
          }
        : { method: 'productTile', component: componentSource },
      !isDirectClick || !shouldOpenPdp,
    );
  });

  const handleOnCashFundProductClick = useEventCallback<React.MouseEventHandler<HTMLButtonElement>>(e => {
    closeDrawer();
    handleClick(e);
  });

  const showProductDetailPage = (shopItemClickedArgs: ShopItemClickedFromComponent, skipRedirection: boolean = false) => {
    const isProductRec = shopItemClickedArgs.component === 'productRec';
    const queryParams = new URLSearchParams(location.search);

    if (isProductRec) {
      queryParams.append('productRec', 'true');
    }

    const isOverviewProductRecommendations = isOverviewRouteMatch && isProductRec;
    setClicked(true);

    if (selectedProduct.id && !skipRedirection) {
      goToCatalogItem(selectedProduct.id, 'push', queryParams.toString());
    }

    trackProductTileClickAction({
      product: selectedProduct,
      position: index,
      page: telemetryPageValue,
      ...(isOverviewProductRecommendations && { category: 'registry.overview' }),
      ...shopItemClickedArgs,
    });
  };

  useEffect(() => {
    if (!addItemLoading) {
      setClicked(false);
    }
  }, [addItemLoading]);

  return {
    changeActiveDimensionOption,
    handleDetailClick,
    handleDetailKeyPress,
    handleMobileQuickAddClick,
    handleOnCashFundProductClick,
    handleOnViewMoreDimensionClick,
    isAddLoading: addItemLoading && clicked,
    productDisplayFields,
    formatPrice,
    selectedProduct,
    swatchColorOptions,
    setProductSelected: setProductDimension,
    isTypeCashOrCharity,
    isProductInRegistry,
    onOpenEditGiftDialog,
  };
};
